import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CInput, CLoader, SEO } from '../../components/common';
import { FiSearch, FiChevronLeft, FiChevronRight, FiCalendar, FiClock, FiVideo, FiMessageCircle, FiShield, FiBriefcase, FiGlobe, FiFilter, FiX, FiCheck } from 'react-icons/fi';
import { IoStar, IoStarOutline } from 'react-icons/io5';
import {
  handleExpertSearch, fetchUserBookings, acceptBooking, cancelBooking,
  fetchSpecializationCategories,
  Expert, Booking, ExpertSearchFilters, SpecializationCategory,
} from '../../api';

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'experience', label: 'Most Experienced' },
];

const ExpertSearch: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedTab, setSelectedTab] = useState<'search' | 'bookings' | 'sessions'>('search');
  const [experts, setExperts] = useState<Expert[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalExperts, setTotalExperts] = useState(0);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState<Record<string, SpecializationCategory[]>>({});
  const [selectedSpecs, setSelectedSpecs] = useState<string[]>([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [maxRate, setMaxRate] = useState<number>(0);
  const [sortBy, setSortBy] = useState('relevance');

  useEffect(() => {
    fetchSpecializationCategories()
      .then(setCategories)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedTab === 'search') {
      fetchExperts();
    } else {
      loadBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, page, selectedSpecs, verifiedOnly, maxRate, sortBy]);

  const buildFilters = (): ExpertSearchFilters => {
    const filters: ExpertSearchFilters = {};
    if (selectedSpecs.length) filters.specializations = selectedSpecs;
    if (verifiedOnly) filters.verified_only = true;
    if (maxRate > 0) filters.max_rate = maxRate;
    if (sortBy !== 'relevance') filters.sort_by = sortBy;
    return filters;
  };

  const fetchExperts = async (query?: string) => {
    setLoading(true);
    try {
      const response = await handleExpertSearch(query || searchText || '', page, buildFilters());
      setExperts(response.experts);
      setTotalPages(response.total_pages || 1);
      setTotalExperts(response.total_experts || 0);
    } catch (error) {
      console.error('Error fetching experts:', error);
      setExperts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      const data = await fetchUserBookings();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchExperts(text);
    }, 500);
  };

  const toggleSpec = (slug: string) => {
    setPage(1);
    setSelectedSpecs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const clearFilters = () => {
    setSelectedSpecs([]);
    setVerifiedOnly(false);
    setMaxRate(0);
    setSortBy('relevance');
    setPage(1);
  };

  const hasActiveFilters = selectedSpecs.length > 0 || verifiedOnly || maxRate > 0 || sortBy !== 'relevance';

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    const endDate = new Date(date.getTime() + 30 * 60 * 1000);
    return `${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const isUpcoming = (booking: Booking) => {
    const bookingEndTime = new Date(booking.start_time).getTime() + 30 * 60 * 1000;
    return bookingEndTime > Date.now();
  };

  const canJoin = (booking: Booking) => {
    const now = Date.now();
    const startTime = new Date(booking.start_time).getTime();
    const endTime = startTime + 30 * 60 * 1000;
    return now >= startTime - 5 * 60 * 1000 && now <= endTime;
  };

  const handleJoinSession = (booking: Booking) => {
    if (booking.booking_type === 'V') {
      navigate(`/call/${booking.id}?expert=${encodeURIComponent(booking.expert)}&expertId=${booking.expert_id}`);
    } else {
      navigate(`/chat/${booking.id}?expert=${encodeURIComponent(booking.expert)}&expertAgoraId=${booking.expert_agora_id || ''}&expertId=${booking.expert_id}`);
    }
  };

  const handleAcceptBooking = async (bookingId: number) => {
    setLoading(true);
    try {
      await acceptBooking(bookingId);
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, is_booked: true } : b))
      );
    } catch (error) {
      console.error('Error accepting booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = (bookingId: number) => {
    setSelectedBookingId(bookingId);
    setCancelModalOpen(true);
  };

  const confirmCancelBooking = async () => {
    if (!selectedBookingId) return;
    setLoading(true);
    try {
      await cancelBooking(selectedBookingId);
      setBookings((prev) => prev.filter((b) => b.id !== selectedBookingId));
      setCancelModalOpen(false);
      setSelectedBookingId(null);
    } catch (error) {
      console.error('Error canceling booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = (booking: Booking) => {
    navigate(`/expert/${booking.expert_id}/book?type=${booking.booking_type === 'V' ? 'call' : 'chat'}&reschedule=${booking.id}`);
  };

  const handleViewSession = (booking: Booking) => {
    if (booking.is_rated) {
      navigate(`/expert/${booking.expert_id}`);
    } else {
      navigate(`/rate/${booking.id}?expert=${encodeURIComponent(booking.expert)}&expertId=${booking.expert_id}`);
    }
  };

  const upcomingBookings = bookings.filter(isUpcoming);
  const pastSessions = bookings.filter((b) => !isUpcoming(b));

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i}>
          {React.createElement(
            i < Math.floor(rating) ? (IoStar as any) : (IoStarOutline as any),
            { size: 12, color: i < Math.floor(rating) ? '#FFD700' : colors.grayScale400 }
          )}
        </span>
      );
    }
    return <div className="flex gap-0.5">{stars}</div>;
  };

  const minRate = (expert: Expert) =>
    Math.min(expert.call_session_rate || 99999, expert.chat_session_rate || 99999);

  const getAvailabilityBadge = (expert: Expert) => {
    const hours = expert.hours_until_available;
    if (hours === null || hours === undefined) {
      return { text: 'Check availability', color: colors.grayScale400, dot: colors.grayScale400 };
    }
    if (hours <= 2) {
      return { text: 'Available in 2h', color: '#22c55e', dot: '#22c55e' };
    }
    if (hours <= 24) {
      return { text: 'Available today', color: '#22c55e', dot: '#22c55e' };
    }
    if (hours <= 72) {
      return { text: 'Available this week', color: '#f59e0b', dot: '#f59e0b' };
    }
    return { text: 'Check availability', color: colors.grayScale400, dot: colors.grayScale400 };
  };

  const ExpertCard = ({ expert }: { expert: Expert }) => {
    const availability = getAvailabilityBadge(expert);

    return (
      <div
        className="relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
        style={{
          backgroundColor: colors.cardBackground || colors.inputBackground,
          border: `1px solid ${colors.borderColor}`,
          boxShadow: colors.shadowSoft,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.boxShadow = colors.shadowMedium || '';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.boxShadow = colors.shadowSoft || '';
        }}
        onClick={() => navigate(`/expert/${expert.user_id}`)}
      >
        {/* Verified accent bar */}
        {expert.is_verified && (
          <div
            className="absolute top-0 left-0 right-0 h-[2px]"
            style={{
              background: `linear-gradient(90deg, ${colors.gradient1}, ${colors.gradient2})`,
            }}
          />
        )}

        <div className="p-4">
          {/* Top: Avatar + Name + Availability */}
          <div className="flex items-start gap-3 mb-3">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0"
              style={{
                background: expert.is_verified
                  ? `linear-gradient(135deg, ${colors.gradient1}, ${colors.gradient2})`
                  : colors.primary,
                color: colors.white,
              }}
            >
              {expert.image ? (
                <img
                  src={expert.image}
                  alt={expert.name}
                  className="w-14 h-14 rounded-full object-cover"
                />
              ) : (
                expert.name.charAt(0)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <CText type="B16" color={colors.textColor} className="truncate">
                  {expert.name}
                </CText>
                {expert.is_verified &&
                  React.createElement(FiShield as any, {
                    size: 14,
                    color: colors.primary,
                    className: 'flex-shrink-0',
                  })
                }
              </div>
              {expert.company_associated && expert.company_name && (
                <CText type="r12" color={colors.grayScale400} className="truncate">
                  {expert.company_name}
                </CText>
              )}
              {/* Availability badge */}
              <div className="flex items-center gap-1.5 mt-1">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: availability.dot }}
                />
                <span className="text-[11px] font-medium" style={{ color: availability.color }}>
                  {availability.text}
                </span>
              </div>
            </div>
          </div>

          {/* Headline */}
          {expert.headline && (
            <CText type="M12" color={colors.grayScale500} className="mb-2 line-clamp-1">
              {expert.headline}
            </CText>
          )}

          {/* Specialization chips */}
          {expert.specializations && expert.specializations.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {expert.specializations.slice(0, 3).map((slug) => (
                <span
                  key={slug}
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                  style={{
                    backgroundColor: colors.primary + '15',
                    color: colors.primary,
                  }}
                >
                  {slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </span>
              ))}
              {expert.specializations.length > 3 && (
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                  style={{ backgroundColor: colors.primary + '10', color: colors.primary }}
                >
                  +{expert.specializations.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Credential badges */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {expert.sebi_qualification && (
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-full"
                style={{ backgroundColor: colors.primary50 }}
              >
                {React.createElement(FiShield as any, { size: 10, color: colors.primary })}
                <span className="text-[10px] font-medium" style={{ color: colors.primary }}>
                  {expert.sebi_qualification}
                </span>
              </div>
            )}
            {expert.years_of_xp > 0 && (
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-full"
                style={{ backgroundColor: colors.primary50 }}
              >
                {React.createElement(FiBriefcase as any, { size: 10, color: colors.primary })}
                <span className="text-[10px] font-medium" style={{ color: colors.primary }}>
                  {expert.years_of_xp} yrs
                </span>
              </div>
            )}
            {expert.total_completed_sessions > 0 && (
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-full"
                style={{ backgroundColor: colors.primary50 }}
              >
                <span className="text-[10px] font-medium" style={{ color: colors.primary }}>
                  {expert.total_completed_sessions} sessions
                </span>
              </div>
            )}
            {expert.languages && expert.languages.length > 0 && (
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-full"
                style={{ backgroundColor: colors.primary50 }}
              >
                {React.createElement(FiGlobe as any, { size: 10, color: colors.primary })}
                <span className="text-[10px] font-medium" style={{ color: colors.primary }}>
                  {expert.languages.join(', ')}
                </span>
              </div>
            )}
          </div>

          {/* Call accuracy badge */}
          {(expert.calls_accuracy_pct || 0) > 0 && (expert.total_calls_made || 0) >= 5 && (
            <div className="flex items-center gap-1.5 mb-2">
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                style={{ backgroundColor: '#22c55e20', color: '#22c55e' }}
              >
                {expert.calls_accuracy_pct?.toFixed(0)}% accuracy ({expert.total_calls_made} calls)
              </span>
            </div>
          )}

          {/* Match reasons */}
          {expert.match_reasons && expert.match_reasons.length > 0 && (
            <p className="text-[11px] mb-2 line-clamp-1" style={{ color: colors.grayScale500 }}>
              {expert.match_reasons.slice(0, 2).join(' | ')}
            </p>
          )}
        </div>

        {/* Bottom: Rating + Price */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderTop: `1px solid ${colors.borderColor}` }}
        >
          <div className="flex items-center gap-1.5">
            {expert.user_avg_rating > 0 ? (
              <>
                {renderStars(expert.user_avg_rating)}
                <CText type="M12" color={colors.textColor}>
                  {expert.user_avg_rating.toFixed(1)}
                </CText>
                {expert.total_ratings_count > 0 && (
                  <CText type="r12" color={colors.grayScale400}>
                    ({expert.total_ratings_count})
                  </CText>
                )}
              </>
            ) : (
              <CText type="r12" color={colors.grayScale400}>
                No ratings yet
              </CText>
            )}
          </div>
          <CText type="B14" color={colors.primary}>
            From Rs.{minRate(expert)}/session
          </CText>
        </div>
      </div>
    );
  };

  const allSpecs = Object.values(categories).flat();

  const tabs = [
    { key: 'search', label: 'Expert Search' },
    { key: 'bookings', label: 'Bookings' },
    { key: 'sessions', label: 'Sessions' },
  ];

  return (
    <div
      className="min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO
        title="Expert Opinion — SEBI-Registered Investment Experts"
        description="Connect with SEBI-registered financial experts for personalized investment advice. Book chat or call sessions with certified advisors on Yantrai."
        canonical="/experts"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ProfessionalService',
          name: 'Yantrai Expert Advisors',
          description: 'SEBI-registered financial experts offering personalized investment advice via chat and call sessions.',
          url: 'https://yantrai.com/experts',
        }}
      />
      <div className="lg:max-w-5xl lg:mx-auto">
        {/* Gradient Hero Banner */}
        <div
          className="rounded-none lg:rounded-2xl p-6 md:p-8 mb-5"
          style={{
            background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 50%, ${colors.gradient1} 100%)`,
          }}
        >
          <CText type="B24" color={colors.white}>
            Expert Opinion
          </CText>
          <CText type="r14" color="rgba(255,255,255,0.8)" className="mt-1">
            Connect with SEBI-registered financial experts
          </CText>
          {totalExperts > 0 && (
            <div
              className="inline-flex items-center px-3 py-1 rounded-full mt-3"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            >
              <CText type="M12" color={colors.white}>
                {totalExperts} expert{totalExperts !== 1 ? 's' : ''} available
              </CText>
            </div>
          )}
        </div>

        <div className="px-4 lg:px-0">
          {/* Tab Selector */}
          <div
            className="flex rounded-full p-1 mb-5"
            style={{
              backgroundColor: colors.inputBackground,
              boxShadow: colors.shadowSoft,
            }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className="flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: selectedTab === tab.key ? colors.primary : 'transparent',
                  color: selectedTab === tab.key ? colors.white : colors.grayScale400,
                  boxShadow: selectedTab === tab.key ? colors.shadowMedium : 'none',
                }}
                onClick={() => setSelectedTab(tab.key as typeof selectedTab)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Bookings Tab */}
          {selectedTab === 'bookings' && (
            <div>
              {loading ? (
                <div className="flex justify-center py-16">
                  <CLoader />
                </div>
              ) : upcomingBookings.length === 0 ? (
                <div className="text-center py-12">
                  <CText type="r14" color={colors.grayScale500}>
                    No upcoming bookings
                  </CText>
                  <button
                    onClick={() => setSelectedTab('search')}
                    className="mt-4 px-6 py-2 rounded-full"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <CText type="M14" color={colors.white}>
                      Book an Expert
                    </CText>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {upcomingBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="rounded-xl p-4"
                      style={{ backgroundColor: colors.inputBackground }}
                    >
                      {/* Header Row */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="p-2 rounded-full"
                            style={{ backgroundColor: colors.primary + '20' }}
                          >
                            {React.createElement(
                              booking.booking_type === 'V' ? FiVideo : FiMessageCircle as any,
                              { size: 16, color: colors.primary }
                            )}
                          </div>
                          <CText type="B16" color={colors.textColor}>
                            {booking.expert}
                          </CText>
                        </div>
                        {!booking.is_booked && (
                          <span
                            className="px-2 py-1 rounded text-xs"
                            style={{ backgroundColor: colors.alertColor + '20', color: colors.alertColor }}
                          >
                            Pending
                          </span>
                        )}
                      </div>

                      {/* Date & Time */}
                      <div className="flex items-center gap-4 mb-3">
                        <div className="flex items-center gap-2">
                          {React.createElement(FiCalendar as any, { size: 14, color: colors.grayScale500 })}
                          <CText type="r14" color={colors.textColor}>
                            {formatDate(booking.start_time)}
                          </CText>
                        </div>
                        <div className="flex items-center gap-2">
                          {React.createElement(FiClock as any, { size: 14, color: colors.grayScale500 })}
                          <CText type="r14" color={colors.textColor}>
                            {formatTime(booking.start_time)}
                          </CText>
                        </div>
                      </div>

                      {/* Join Link */}
                      {booking.is_booked && (
                        <button
                          onClick={() => handleJoinSession(booking)}
                          disabled={!canJoin(booking)}
                          className="mb-3"
                          style={{
                            opacity: canJoin(booking) ? 1 : 0.5,
                            cursor: canJoin(booking) ? 'pointer' : 'not-allowed',
                          }}
                        >
                          <CText type="M14" color={colors.primary}>
                            {canJoin(booking)
                              ? booking.booking_type === 'V'
                                ? 'Join Call →'
                                : 'Start Chat →'
                              : 'Waiting for session time...'}
                          </CText>
                        </button>
                      )}

                      {/* Action Buttons */}
                      {!booking.is_booked && (
                        <div className="flex gap-3 mt-2">
                          {(user as any)?.is_expert ? (
                            <button
                              onClick={() => handleAcceptBooking(booking.id)}
                              className="flex-1 py-2 rounded-lg border"
                              style={{ borderColor: colors.primary }}
                            >
                              <CText type="M14" color={colors.primary}>
                                Accept
                              </CText>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReschedule(booking)}
                              className="flex-1 py-2 rounded-lg border"
                              style={{ borderColor: colors.borderColor }}
                            >
                              <CText type="M14" color={colors.primary}>
                                Re-Schedule
                              </CText>
                            </button>
                          )}
                          <button
                            onClick={() => handleCancelBooking(booking.id)}
                            className="flex-1 py-2 rounded-lg border"
                            style={{ borderColor: colors.borderColor }}
                          >
                            <CText type="M14" color={colors.alertColor}>
                              Cancel
                            </CText>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sessions Tab (Past) */}
          {selectedTab === 'sessions' && (
            <div>
              {loading ? (
                <div className="flex justify-center py-16">
                  <CLoader />
                </div>
              ) : pastSessions.length === 0 ? (
                <div className="text-center py-12">
                  <CText type="r14" color={colors.grayScale500}>
                    No past sessions
                  </CText>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pastSessions.map((booking) => (
                    <button
                      key={booking.id}
                      onClick={() => handleViewSession(booking)}
                      className="w-full rounded-xl p-4 text-left"
                      style={{ backgroundColor: colors.inputBackground }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="p-2 rounded-full"
                            style={{ backgroundColor: colors.grayScale500 + '20' }}
                          >
                            {React.createElement(
                              booking.booking_type === 'V' ? FiVideo : FiMessageCircle as any,
                              { size: 16, color: colors.grayScale500 }
                            )}
                          </div>
                          <div>
                            <CText type="M14" color={colors.textColor}>
                              {booking.expert}
                            </CText>
                            <div className="flex items-center gap-2">
                              <CText type="r12" color={colors.grayScale500}>
                                {formatDate(booking.start_time)}
                              </CText>
                              <CText type="r12" color={colors.grayScale500}>
                                {formatTime(booking.start_time)}
                              </CText>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {booking.is_rated ? (
                            <span
                              className="px-2 py-1 rounded text-xs"
                              style={{ backgroundColor: colors.green + '20', color: colors.green }}
                            >
                              Rated
                            </span>
                          ) : (
                            <span
                              className="px-2 py-1 rounded text-xs"
                              style={{ backgroundColor: colors.primary + '20', color: colors.primary }}
                            >
                              Rate →
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Expert Search Tab */}
          {selectedTab === 'search' && (
            <>
              {/* Search + Filter toggle */}
              <div className="flex gap-2 mb-4">
                <div className="flex-1" style={{ boxShadow: colors.shadowMedium, borderRadius: 12 }}>
                  <CInput
                    value={searchText}
                    onChange={handleSearchChange}
                    placeholder="Search experts by name, specialization..."
                    insideLeftIcon={() =>
                      React.createElement(FiSearch as any, { size: 18, color: colors.grayScale500 })
                    }
                    className="lg:!max-w-full"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 relative"
                  style={{
                    backgroundColor: showFilters ? colors.primary : colors.inputBackground,
                    border: `1px solid ${colors.borderColor}`,
                  }}
                >
                  {React.createElement(FiFilter as any, {
                    size: 18,
                    color: showFilters ? colors.white : colors.grayScale500,
                  })}
                  {hasActiveFilters && (
                    <div
                      className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
                      style={{ backgroundColor: colors.primary }}
                    />
                  )}
                </button>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div
                  className="rounded-xl p-4 mb-4"
                  style={{
                    backgroundColor: colors.cardBackground || colors.inputBackground,
                    border: `1px solid ${colors.borderColor}`,
                  }}
                >
                  {/* Specialization chips */}
                  <div className="mb-4">
                    <CText type="M12" color={colors.grayScale500} className="mb-2">
                      Specializations
                    </CText>
                    <div className="flex flex-wrap gap-2">
                      {allSpecs.map((cat) => {
                        const isSelected = selectedSpecs.includes(cat.slug);
                        return (
                          <button
                            key={cat.slug}
                            onClick={() => toggleSpec(cat.slug)}
                            className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                            style={{
                              backgroundColor: isSelected ? colors.primary : 'transparent',
                              color: isSelected ? colors.white : colors.textColor,
                              border: `1px solid ${isSelected ? colors.primary : colors.borderColor}`,
                            }}
                          >
                            {cat.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Budget slider */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <CText type="M12" color={colors.grayScale500}>
                        Max per session
                      </CText>
                      <CText type="M12" color={colors.textColor}>
                        {maxRate > 0 ? `Rs.${maxRate}` : 'Any'}
                      </CText>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={5000}
                      step={100}
                      value={maxRate}
                      onChange={(e) => {
                        setMaxRate(Number(e.target.value));
                        setPage(1);
                      }}
                      className="w-full accent-purple-600"
                    />
                    <div className="flex justify-between text-[10px]" style={{ color: colors.grayScale400 }}>
                      <span>Any</span>
                      <span>Rs.5000</span>
                    </div>
                  </div>

                  {/* Verified toggle + Sort */}
                  <div className="flex items-center gap-4 flex-wrap">
                    <button
                      onClick={() => { setVerifiedOnly(!verifiedOnly); setPage(1); }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg"
                      style={{
                        backgroundColor: verifiedOnly ? colors.primary + '15' : 'transparent',
                        border: `1px solid ${verifiedOnly ? colors.primary : colors.borderColor}`,
                      }}
                    >
                      <div
                        className="w-4 h-4 rounded flex items-center justify-center"
                        style={{
                          backgroundColor: verifiedOnly ? colors.primary : 'transparent',
                          border: `1.5px solid ${verifiedOnly ? colors.primary : colors.grayScale400}`,
                        }}
                      >
                        {verifiedOnly && React.createElement(FiCheck as any, { size: 10, color: '#fff' })}
                      </div>
                      <span className="text-xs font-medium" style={{ color: colors.textColor }}>
                        SEBI Verified only
                      </span>
                    </button>

                    <select
                      value={sortBy}
                      onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                      className="px-3 py-2 rounded-lg text-xs font-medium"
                      style={{
                        backgroundColor: colors.inputBackground,
                        color: colors.textColor,
                        border: `1px solid ${colors.borderColor}`,
                        outline: 'none',
                      }}
                    >
                      {SORT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium"
                        style={{ color: colors.alertColor }}
                      >
                        {React.createElement(FiX as any, { size: 12 })}
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center py-16">
                  <CLoader />
                </div>
              ) : experts.length === 0 ? (
                /* Empty State */
                <div className="flex flex-col items-center justify-center py-16">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: colors.primary50 }}
                  >
                    {React.createElement(FiSearch as any, { size: 28, color: colors.primary })}
                  </div>
                  <CText type="B18" color={colors.textColor} className="mb-2">
                    No experts match your criteria
                  </CText>
                  <CText type="r14" color={colors.grayScale500} className="mb-4">
                    Try adjusting your filters or search term
                  </CText>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="px-6 py-2 rounded-full"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <CText type="M14" color={colors.white}>
                        Clear filters
                      </CText>
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Expert List */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {experts.map((expert) => (
                      <ExpertCard key={expert.user_id} expert={expert} />
                    ))}
                  </div>

                  {/* Pagination at bottom */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-4 mt-6">
                      <button
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: page === 1 ? colors.inputBackground : colors.primary,
                          color: page === 1 ? colors.grayScale400 : colors.white,
                          opacity: page === 1 ? 0.6 : 1,
                        }}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        {React.createElement(FiChevronLeft as any, { size: 16, style: { display: 'inline', verticalAlign: 'middle' } })}
                        {' '}Prev
                      </button>
                      <CText type="M14" color={colors.textColor}>
                        Page {page} of {totalPages}
                      </CText>
                      <button
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: page === totalPages ? colors.inputBackground : colors.primary,
                          color: page === totalPages ? colors.grayScale400 : colors.white,
                          opacity: page === totalPages ? 0.6 : 1,
                        }}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Next{' '}
                        {React.createElement(FiChevronRight as any, { size: 16, style: { display: 'inline', verticalAlign: 'middle' } })}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="rounded-2xl p-6 w-full max-w-sm"
            style={{ backgroundColor: colors.backgroundColor }}
          >
            <CText type="B18" color={colors.textColor} style={{ marginBottom: 8 }}>
              Cancel Booking?
            </CText>
            <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 24 }}>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </CText>
            <div className="flex gap-3">
              <button
                onClick={() => setCancelModalOpen(false)}
                className="flex-1 py-3 rounded-xl border"
                style={{ borderColor: colors.borderColor }}
              >
                <CText type="M14" color={colors.textColor}>
                  Keep Booking
                </CText>
              </button>
              <button
                onClick={confirmCancelBooking}
                className="flex-1 py-3 rounded-xl"
                style={{ backgroundColor: colors.alertColor }}
              >
                <CText type="M14" color={colors.white}>
                  Cancel
                </CText>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertSearch;
